import React, { useState } from 'react';
import {
    Button as MuiButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import {
    Block,
    FlexDiv,
    Heading2,
    Heading3,
    SpaceBetweenDiv,
    TextDefault,
    TextLead,
    TextTiny,
    TextWithTooltipIcon,
    palette,
} from '@awarego-ui/components';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import pluralize from 'pluralize';
import { useStores } from '../../../../hooks/use-stores';
import { useAssessmentType } from '../../useAssessmentType';
import SubHeaderSeparator from '../../../../components/header/SubHeaderSeparator';
import PreviewSyncLists from '../../../automations/SingleAutomation/PreviewSyncLists';

const SyncStatusRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 100%;
`;

function EmployeeListSync({ editingAssessment, companyId, assessmentId }) {
    const { assessmentStore } = useAssessmentType();
    const { commonStore, store } = useStores();
    const [manualSyncOpen, setManualSyncOpen] = useState(false);

    const usersToAddCount =
        assessmentStore.previewUsersToAddSelection
            .totalSelectionCountOnAllPages;
    const usersToRemoveCount =
        assessmentStore.previewUsersToRemoveSelection
            .totalSelectionCountOnAllPages;
    const hasSyncPreview =
        assessmentStore.previewUsersToAdd.length > 0 ||
        assessmentStore.previewUsersToRemove.length > 0;

    const handleManualSyncLists = () => {
        assessmentStore.previewUsersToRemoveSelection.clearSelection();
        assessmentStore.previewUsersToAddSelection.clearSelection();
        setManualSyncOpen(true);
    };

    const handleSyncLists = async () => {
        if (usersToAddCount === 0 && usersToRemoveCount === 0) {
            commonStore.warn('Select at least one user to sync');
            return;
        }
        const result = await assessmentStore.syncUsers(
            companyId,
            assessmentId
        );
        if (result) {
            commonStore.success('Users synced');
            setManualSyncOpen(false);
        }
    };

    const handleSyncAllLists = async () => {
        assessmentStore.previewUsersToAddSelection.setAllPagesSelected();
        assessmentStore.previewUsersToRemoveSelection.setAllPagesSelected();
        await assessmentStore.syncUsers(companyId, assessmentId);
        await assessmentStore.loadPreviewSyncLists(companyId, assessmentId);
    };

    const handleSwitchSyncLists = async () => {
        const action = editingAssessment.disableSyncLists ? 'Enable' : 'Disable';
        await assessmentStore.loadPreviewSyncLists(companyId, assessmentId);
        if (editingAssessment.disableSyncLists) {
            assessmentStore.previewUsersToAddSelection.clearSelection();
            assessmentStore.previewUsersToRemoveSelection.clearSelection();
        }

        commonStore.showConfirm(
            <>
                {editingAssessment.disableSyncLists
                    ? 'Would you like to activate automatic synchronization of the user list? Enabling this feature ensures that any modifications to the user lists are immediately updated in this assessment.'
                    : 'Would you like to disable automatic synchronization of the user list? If you disable this feature, changes to the user lists will not be automatically updated in this assessment. However, you can still synchronize the lists manually.'}
                {editingAssessment.disableSyncLists && (
                    <FlexDiv pt={16} fullWidth column>
                        <PreviewSyncLists
                            automationId={assessmentId}
                            companyId={companyId}
                            selectable={false}
                            syncStore={assessmentStore}
                        />
                    </FlexDiv>
                )}
                {!editingAssessment.disableSyncLists &&
                    assessmentStore.previewSyncLists.some(
                        (x) => x.added || x.removed
                    ) && (
                        <FlexDiv column pt={16} gap={16}>
                            <FlexDiv column gap={8}>
                                <Heading3>Sync Required</Heading3>
                                <TextTiny>
                                    New Users:{' '}
                                    {
                                        assessmentStore.previewSyncLists.filter(
                                            (x) => x.added
                                        ).length
                                    }
                                </TextTiny>
                                <TextTiny>
                                    Obsolete Users:{' '}
                                    {
                                        assessmentStore.previewSyncLists.filter(
                                            (x) => x.removed
                                        ).length
                                    }
                                </TextTiny>
                            </FlexDiv>
                            <FlexDiv justifyCenter>
                                <MuiButton
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                        await handleSyncAllLists();
                                        commonStore.cancelConfirm();
                                    }}
                                >
                                    Sync Now
                                </MuiButton>
                            </FlexDiv>
                        </FlexDiv>
                    )}
            </>,
            action,
            `${action} user list sync`,
            async () => {
                const result = await assessmentStore.switchSyncLists(
                    companyId,
                    assessmentId,
                    !editingAssessment.disableSyncLists
                );
                if (result) {
                    commonStore.success('User list sync switched');
                }
            }
        );
    };

    if (editingAssessment.isScorm) return null;

    return (
        <>
            <Block gap={16} fullWidth alignStretch>
                <TextWithTooltipIcon
                    textComponent={<Heading3>Employee list sync</Heading3>}
                    tooltipText={
                        editingAssessment.disableSyncLists
                            ? 'User sync is inactive. Changes to the employee lists will not be automatically updated in this assessment.'
                            : 'User sync is active. Users added to user lists in this assessment are automatically added to this assessment.'
                    }
                />
                <SyncStatusRow>
                    <Heading2
                        style={{
                            borderBottom: store.isAdmin
                                ? `1px dotted ${
                                      editingAssessment.disableSyncLists
                                          ? palette.vibrant.orange
                                          : palette.evergreen.aspargus
                                  }`
                                : undefined,
                        }}
                        link={store.isAdmin || undefined}
                        onClick={
                            store.isAdmin ? handleSwitchSyncLists : undefined
                        }
                        color={
                            editingAssessment.disableSyncLists
                                ? palette.vibrant.orange
                                : palette.evergreen.aspargus
                        }
                    >
                        {editingAssessment.disableSyncLists
                            ? 'Inactive'
                            : 'Active'}
                    </Heading2>
                    {editingAssessment.disableSyncLists && store.isAdmin && (
                        <>
                            <SubHeaderSeparator />
                            <TextTiny link onClick={handleManualSyncLists}>
                                Sync users
                            </TextTiny>
                        </>
                    )}
                </SyncStatusRow>
            </Block>
            <Dialog
                maxWidth="md"
                fullWidth
                open={manualSyncOpen}
                onClose={() => setManualSyncOpen(false)}
            >
                <DialogTitle>Sync users</DialogTitle>
                <DialogContent>
                    <FlexDiv column fullWidth gap={16}>
                        <TextLead style={{ display: 'block' }}>
                            Would you like to synchronize the user list now?
                            Selected new users will be added to this
                            assessment, and selected obsolete users will be
                            removed.
                        </TextLead>
                        <PreviewSyncLists
                            automationId={assessmentId}
                            companyId={companyId}
                            selectable
                            syncStore={assessmentStore}
                        />
                    </FlexDiv>
                </DialogContent>
                <DialogActions>
                    <SpaceBetweenDiv gap={24} alignCenter px={16} py={8}>
                        <FlexDiv gap={8} alignCenter>
                            {hasSyncPreview && (
                                <>
                                    <TextTiny
                                        bold
                                        color={palette.evergreen.aspargus}
                                    >
                                        {usersToAddCount}{' '}
                                        {pluralize('user', usersToAddCount)}{' '}
                                        will be added.
                                    </TextTiny>{' '}
                                    <TextTiny
                                        bold
                                        color={palette.vibrant.orange}
                                    >
                                        {usersToRemoveCount}{' '}
                                        {pluralize('user', usersToRemoveCount)}{' '}
                                        will be removed.
                                    </TextTiny>
                                </>
                            )}
                        </FlexDiv>
                        <FlexDiv gap={8} alignCenter>
                            <TextDefault
                                uppercase
                                extraBold
                                link
                                onClick={() => setManualSyncOpen(false)}
                            >
                                Close
                            </TextDefault>
                            {hasSyncPreview && (
                                <MuiButton
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        usersToAddCount === 0 &&
                                        usersToRemoveCount === 0
                                    }
                                    onClick={handleSyncLists}
                                >
                                    Sync Now
                                </MuiButton>
                            )}
                        </FlexDiv>
                    </SpaceBetweenDiv>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default observer(EmployeeListSync);
